import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SlideRenderer from './SlideRenderer';
import type { PresentationSlide } from '../types';

/**
 * Miniatuur van een slide in de slide-lijst. De afbeelding wordt pas opgehaald
 * zodra de miniatuur in beeld komt: een geïmporteerde presentatie kan tot 100
 * pagina's hebben en die hoeven niet allemaal tegelijk geladen te worden.
 * Zonder IntersectionObserver (oude browsers, tests) wordt direct geladen.
 */
export function LazySlidePreview({ slide }: { slide: PresentationSlide }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (visible || !ref.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div ref={ref} className="aspect-video w-full overflow-hidden rounded-md border border-slate-200 bg-slate-100">
      {visible ? <SlideRenderer slide={slide} size="preview" previewMode /> : null}
    </div>
  );
}

interface DialogProps {
  slides: PresentationSlide[];
  /** Index van de getoonde slide; null = gesloten. */
  index: number | null;
  onIndexChange: (index: number | null) => void;
}

/**
 * Groot voorbeeld van een slide zoals de beamer hem toont (zwarte achtergrond,
 * volledige weergave), met vorige/volgende en pijltjestoetsen. Toont alleen
 * wat de slide zelf bevat; nooit privé-notities.
 */
export default function SlidePreviewDialog({ slides, index, onIndexChange }: DialogProps) {
  const open = index !== null && slides[index] !== undefined;
  const current = open ? slides[index as number] : null;

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') onIndexChange(Math.min(slides.length - 1, (index as number) + 1));
      if (event.key === 'ArrowLeft') onIndexChange(Math.max(0, (index as number) - 1));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, index, slides.length, onIndexChange]);

  return (
    <Dialog open={open} onOpenChange={(next: boolean) => { if (!next) onIndexChange(null); }}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Voorbeeld: slide {open ? (index as number) + 1 : 0} van {slides.length}</DialogTitle>
        </DialogHeader>
        <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
          {current && <SlideRenderer slide={current} size="full" previewMode />}
        </div>
        <div className="flex items-center justify-between">
          <Button
            type="button" variant="outline" onClick={() => onIndexChange((index as number) - 1)}
            disabled={!open || (index as number) <= 0}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Vorige
          </Button>
          <Button
            type="button" variant="outline" onClick={() => onIndexChange((index as number) + 1)}
            disabled={!open || (index as number) >= slides.length - 1}
          >
            Volgende
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
